import React, { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import {Menu, X, User, LogOut, Settings} from 'lucide-react'
import { useAuth } from '@/hooks/useAuth'
import { lumi } from '@/lib/lumi'
import AuthModal from './AuthModal'
import LanguagePicker from './LanguagePicker'

export default function Navigation() {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false)
  const location = useLocation()
  const { isAuthenticated, user } = useAuth()

  const handleSignOut = async () => {
    await lumi.auth.signOut()
    console.log('User signed out')
  }

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/metal-roofing', label: 'Metal Roofing' },
    { path: '/commercial', label: 'Commercial' },
    { path: '/repair', label: 'Repair' },
    { path: '/virtual-estimate', label: 'Virtual Estimate' },
    { path: '/blog', label: 'Blog' },
    { path: '/blog-management', label: 'Manage Blog', icon: Settings },
  ]

  const isActive = (path: string) => location.pathname === path

  return (
    <>
      <nav className="bg-blue-900 text-white shadow-lg sticky top-0 z-40">
        <div className="container mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <Link to="/" className="text-xl sm:text-2xl font-bold hover:text-orange-400 transition-colors">
              🏠 RoofingCostsCalculator.com
            </Link>

            {/* Language Picker — desktop */}
            <div className="hidden lg:flex items-center mr-2">
              <LanguagePicker />
            </div>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-1">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    isActive(link.path)
                      ? 'bg-orange-500 text-white'
                      : 'text-white hover:bg-blue-800'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              
              {/* Auth Button */}
              {isAuthenticated ? (
                <div className="flex items-center gap-3 ml-4 pl-4 border-l border-blue-700">
                  <Link
                    to="/saved-estimates"
                    className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                      isActive('/saved-estimates')
                        ? 'bg-orange-500 text-white'
                        : 'text-white hover:bg-blue-800'
                    }`}
                  >
                    <User size={18} />
                    {user?.userName || 'My Estimates'}
                  </Link>
                  <button
                    onClick={handleSignOut}
                    className="px-4 py-2 rounded-lg font-medium transition-colors text-white hover:bg-red-600 flex items-center gap-2"
                  >
                    <LogOut size={18} />
                    Sign Out
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setIsAuthModalOpen(true)}
                  className="ml-4 px-6 py-2 bg-orange-500 hover:bg-orange-600 rounded-lg font-semibold transition-colors flex items-center gap-2"
                >
                  <User size={18} />
                  Sign In
                </button>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button
              className="lg:hidden text-white"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label="Toggle menu"
            >
              {isMenuOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>

          {/* Mobile Menu */}
          {isMenuOpen && (
            <div className="lg:hidden mt-4 pb-4 space-y-2">
              {/* Language Picker — mobile */}
              <div className="px-2 py-2 bg-blue-800 rounded-lg">
                <LanguagePicker />
              </div>
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsMenuOpen(false)}
                  className={`block px-4 py-3 rounded-lg font-medium transition-colors ${
                    isActive(link.path)
                      ? 'bg-orange-500 text-white'
                      : 'text-white hover:bg-blue-800'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              
              {/* Mobile Auth Section */}
              {isAuthenticated ? (
                <>
                  <Link
                    to="/saved-estimates"
                    onClick={() => setIsMenuOpen(false)}
                    className={`block px-4 py-3 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                      isActive('/saved-estimates')
                        ? 'bg-orange-500 text-white'
                        : 'text-white hover:bg-blue-800'
                    }`}
                  >
                    <User size={18} />
                    {user?.userName || 'My Estimates'}
                  </Link>
                  <button
                    onClick={() => {
                      handleSignOut()
                      setIsMenuOpen(false)
                    }}
                    className="w-full text-left px-4 py-3 rounded-lg font-medium transition-colors text-white hover:bg-red-600 flex items-center gap-2"
                  >
                    <LogOut size={18} />
                    Sign Out
                  </button>
                </>
              ) : (
                <button
                  onClick={() => {
                    setIsAuthModalOpen(true)
                    setIsMenuOpen(false)
                  }}
                  className="w-full text-left px-4 py-3 bg-orange-500 hover:bg-orange-600 rounded-lg font-semibold transition-colors flex items-center gap-2"
                >
                  <User size={18} />
                  Sign In
                </button>
              )}
            </div>
          )}
        </div>
      </nav>

      <AuthModal 
        isOpen={isAuthModalOpen} 
        onClose={() => setIsAuthModalOpen(false)}
        onSuccess={() => {
          console.log('Auth successful')
        }}
      />
    </>
  )
}
